/**
 * Panel 7: Audit Trail — Every prediction logged with reasoning
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import type { EdgeStatus, AuditEntry } from '../../../types'
import { THREAT_CONFIG, type ThreatLevel } from '../../../types'
import { getEdgeStatus, getAuditEntries } from '../../../services/api'

type AuditThreatFilter = 'ALL' | 'GREEN' | 'AMBER' | 'RED'

const AUDIT_THREAT_FILTERS: { key: AuditThreatFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'GREEN', label: 'Green' },
  { key: 'AMBER', label: 'Amber' },
  { key: 'RED', label: 'Red' },
]

const REFRESH_INTERVAL_MS = 10_000

export function AuditTrailPanel() {
  const [edgeStatus, setEdgeStatus] = useState<EdgeStatus | null>(null)
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [threatFilter, setThreatFilter] = useState<AuditThreatFilter>('ALL')

  const filteredEntries = useMemo(() => {
    if (threatFilter === 'ALL') return entries
    return entries.filter(e => (e.threat_level || '').toUpperCase() === threatFilter)
  }, [entries, threatFilter])

  const fetchData = useCallback(async () => {
    const [edgeRes, auditRes] = await Promise.all([
      getEdgeStatus(),
      getAuditEntries(50),
    ])
    if (edgeRes.data) setEdgeStatus(edgeRes.data)
    if (auditRes.data) {
      setEntries(auditRes.data)
      setError(null)
    } else if (auditRes.error) {
      setError(auditRes.error)
    }
  }, [])

  useEffect(() => {
    fetchData()
    intervalRef.current = setInterval(fetchData, REFRESH_INTERVAL_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [fetchData])

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex items-center justify-between">
        <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
          Decision Audit Trail
        </div>
        <div className="flex items-center gap-3 text-xs text-term-textDim">
          <span>Total predictions: {edgeStatus?.edge_predictions_count ?? 0}</span>
          <span className="text-term-border">|</span>
          <span>Models: {edgeStatus ? Object.keys(edgeStatus.models).length : 0} loaded</span>
          <span className="text-term-border">|</span>
          <span>Entries: {entries.length}</span>
        </div>
      </div>

      {error && (
        <div className="text-xs text-term-error px-2 py-1 border border-term-error/30 rounded">
          Audit fetch error: {error}
        </div>
      )}

      {/* Threat Level Filter */}
      <div className="flex items-center gap-2 border border-term-border/30 rounded p-2 bg-term-bgLight/30">
        <span className="text-[10px] text-term-textDim uppercase tracking-wider font-medium shrink-0">
          Threat
        </span>
        <div className="flex gap-1 flex-wrap">
          {AUDIT_THREAT_FILTERS.map(f => {
            const isActive = threatFilter === f.key
            const tCfg = f.key !== 'ALL' ? THREAT_CONFIG[f.key as ThreatLevel] : null
            return (
              <button
                key={f.key}
                onClick={() => setThreatFilter(f.key)}
                className={`
                  px-2.5 py-1 rounded text-[10px] font-bold transition-all border
                  ${isActive
                    ? tCfg
                      ? `${tCfg.cssClass} ring-2 ring-offset-1 ring-offset-term-bgDark`
                      : 'bg-term-text/20 text-term-text border-term-text/40 ring-2 ring-term-text/30 ring-offset-1 ring-offset-term-bgDark'
                    : 'bg-term-bgDark/50 text-term-textDim border-term-border/20 hover:bg-term-bgLight hover:text-term-text'
                  }
                `}
                style={isActive && tCfg ? { boxShadow: `0 0 8px ${tCfg.color}30` } : {}}
              >
                {f.label}
                {isActive && f.key !== 'ALL' && (
                  <span className="ml-1 opacity-70">
                    ({filteredEntries.length})
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <span className="text-[10px] text-term-textDim ml-auto">
          {filteredEntries.length} of {entries.length}
        </span>
      </div>

      {/* Audit log */}
      <div className="flex flex-col gap-2 overflow-auto flex-1 min-h-0">
        {filteredEntries.map((entry) => {
          const threatColor = entry.threat_level === 'RED' ? 'text-term-error' : entry.threat_level === 'AMBER' ? 'text-term-warning' : 'text-term-success'
          const confidencePct = Math.round(entry.confidence * 100)

          return (
            <div key={entry.id} className="term-box p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-3">
                  <span className="text-term-textDim text-[10px] font-mono tabular-nums">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="text-xs font-bold text-term-primary">{entry.model}</span>
                  <span className="text-xs text-term-text">{entry.asset}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-medium ${threatColor}`}>{entry.threat_level}</span>
                  <span className="text-[10px] text-term-textDim tabular-nums">{confidencePct}%</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-term-textDim">{entry.action}</span>
                <span className="text-term-text font-mono">{entry.result}</span>
              </div>
              <div className="mt-1 h-1 bg-term-bgDark rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${confidencePct}%`,
                    backgroundColor: confidencePct >= 85 ? '#22c55e' : confidencePct >= 70 ? '#f59e0b' : '#ef4444',
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>

      <div className="text-[10px] text-term-textDim px-1">
        Every prediction is logged with model, input hash, output, confidence, and reasoning chain for full auditability.
      </div>
    </div>
  )
}
